// Cost Report — central admin view of what running AND building the app
// costs (internal accounts only).
//
// Two ledgers, one page:
//   • Hosting — AWS daily unblended costs by service, refreshed every 6h by
//     the backend cost worker (api/costWorker.js → costs/aws). Firebase rides
//     the free tier today; noted below rather than pretended at.
//   • Development — per-session Cursor agent token usage (input/output/cache)
//     and billed dollar cost, snapshotted when a run ends and reconciled
//     hourly (billing lands eventually-consistent on Cursor's side).
//
// Reads Firestore directly; everything here is internal-claim gated by rules.
import React, { useEffect, useMemo, useState } from 'react';
import { db } from '../firebaseConfig';
import { isPg, subscribeRows, subscribeRow } from '../data/supabaseClient';
import { resolvePath } from '../data/pathMap';
import { useInternalClaim } from './DevConsole';
import './DevConsole.css';

const fmtUsd = (n) => (n == null ? '—' : `$${n.toFixed(n >= 100 ? 0 : 2)}`);
const fmtTok = (n) => (n == null ? '—' : n >= 1e6 ? `${(n / 1e6).toFixed(1)}M` : n >= 1e3 ? `${(n / 1e3).toFixed(1)}k` : String(n));
const sessionCostUsd = (u) => {
  if (!u) return null;
  const cents = u.chargedCents ?? u.rawCostCents;
  return cents == null ? null : cents / 100;
};

// Both hooks ride the data-plane seam: Firestore on the managed plane,
// doc-table subscriptions (supabaseClient) on the pg plane, where `db` is
// null and a bare `db.collection` would crash the page.
function useAwsCosts() {
  const [doc, setDoc] = useState(null);
  useEffect(() => {
    if (isPg()) return subscribeRow(resolvePath('costs/aws').table, 'aws', (d) => setDoc(d || null), () => setDoc(null));
    return db.collection('costs').doc('aws').onSnapshot(
      (s) => setDoc(s.exists ? s.data() : null),
      () => setDoc(null)
    );
  }, []);
  return doc;
}

function useMonthSessions() {
  const [docs, setDocs] = useState([]);
  useEffect(() => {
    const monthStart = new Date();
    monthStart.setDate(1);
    monthStart.setHours(0, 0, 0, 0);
    const since = monthStart.getTime();
    if (isPg()) {
      return subscribeRows(resolvePath('devSessions').table, [], { orderBy: 'createdAt', dir: 'desc', limit: 200, match: (d) => (d.createdAt || 0) >= since }, setDocs, () => setDocs([]));
    }
    return db.collection('devSessions')
      .where('createdAt', '>=', since)
      .orderBy('createdAt', 'desc')
      .limit(200)
      .onSnapshot((s) => setDocs(s.docs.map((d) => ({ id: d.id, ...d.data() }))), () => setDocs([]));
  }, []);
  return docs;
}

// Smart-routing ledger for the month (backend/shared/devRoutingLedger.js):
// sessions, cost and outcomes per model tier, plus Jev agreement.
function useRoutingLedger() {
  const [doc, setDoc] = useState(null);
  useEffect(() => {
    const d = new Date();
    const period = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
    if (isPg()) return subscribeRow(resolvePath('devRouting/x').table, period, (r) => setDoc(r || null), () => setDoc(null));
    return db.collection('devRouting').doc(period).onSnapshot(
      (s) => setDoc(s.exists ? s.data() : null),
      () => setDoc(null)
    );
  }, []);
  return doc;
}

const TIERS = ['bulk', 'strong', 'frontier'];
const pct = (a, b) => (b ? `${Math.round((100 * a) / b)}%` : '—');

const card = { background: 'var(--bg-elev)', border: '1px solid var(--border)', borderRadius: 12, padding: '16px 20px' };
const h2s = { fontWeight: 700, fontSize: 15, color: 'var(--text)', margin: '0 0 12px' };
const th = { textAlign: 'left', fontWeight: 600, fontSize: 11.5, color: 'var(--muted)', padding: '6px 10px', borderBottom: '1px solid var(--border)' };
const td = { fontWeight: 400, fontSize: 12.5, color: 'var(--text)', padding: '7px 10px', borderBottom: '1px solid var(--border)' };
const tdNum = { ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };

function SummaryCard({ label, value, sub }) {
  return (
    <div style={{ ...card, flex: 1, minWidth: 180 }}>
      <div style={{ fontWeight: 600, fontSize: 11.5, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.04em' }}>{label}</div>
      <div style={{ fontWeight: 700, fontSize: 26, color: 'var(--text)', margin: '6px 0 2px' }}>{value}</div>
      {sub && <div style={{ fontWeight: 400, fontSize: 12, color: 'var(--muted)' }}>{sub}</div>}
    </div>
  );
}

// Pure-CSS daily bar chart (no chart lib needed for 30 bars).
function DailyBars({ days }) {
  const last30 = days.slice(-30);
  const max = Math.max(0.01, ...last30.map((d) => d.total));
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 120, marginTop: 8 }}>
      {last30.map((d) => (
        <div
          key={d.date}
          title={`${d.date}: ${fmtUsd(d.total)}`}
          style={{
            flex: 1,
            height: `${Math.max(2, (d.total / max) * 100)}%`,
            background: 'var(--info)',
            borderRadius: '3px 3px 0 0',
            minWidth: 4,
          }}
        />
      ))}
    </div>
  );
}

export default function CostReport() {
  const internal = useInternalClaim();
  const aws = useAwsCosts();
  const sessions = useMonthSessions();
  const routing = useRoutingLedger();
  const routingRows = useMemo(() => {
    if (!routing) return [];
    return TIERS.map((t) => ({
      tier: t,
      n: routing[`n__${t}`] || 0,
      usd: routing[`usd__${t}`] || 0,
      accepted: routing[`accepted__${t}`] || 0,
      failed: routing[`failed__${t}`] || 0,
    })).filter((r) => r.n > 0);
  }, [routing]);
  const routedTotal = routingRows.reduce((s, r) => s + r.n, 0);
  const escalations = routing ? Object.entries(routing).filter(([k]) => k.startsWith('esc__')).reduce((s, [, v]) => s + (Number(v) || 0), 0) : 0;

  // Month-to-date AWS service breakdown from the daily rows.
  const services = useMemo(() => {
    if (!aws?.days) return [];
    const agg = {};
    aws.days.filter((d) => d.date?.startsWith(aws.monthKey)).forEach((d) => {
      Object.entries(d.services || {}).forEach(([k, v]) => { agg[k] = (agg[k] || 0) + v; });
    });
    return Object.entries(agg).sort((a, b) => b[1] - a[1]);
  }, [aws]);

  const dev = useMemo(() => {
    let cost = 0;
    let costKnown = false;
    let inTok = 0;
    let outTok = 0;
    sessions.forEach((s) => {
      const c = sessionCostUsd(s.usage);
      if (c != null) { cost += c; costKnown = true; }
      inTok += s.usage?.inputTokens || 0;
      outTok += s.usage?.outputTokens || 0;
    });
    return { cost: costKnown ? cost : null, inTok, outTok };
  }, [sessions]);

  if (!internal) {
    return (
      <div className="devc-gate">
        <h2>Cost Report</h2>
        <p>This page is limited to internal accounts. Ask an admin to grant the <code>internal: true</code> custom claim.</p>
      </div>
    );
  }

  const combined = (aws?.monthToDate || 0) + (dev.cost || 0);
  const monthLabel = new Date().toLocaleString('en-US', { month: 'long', year: 'numeric' });

  return (
    <div style={{ maxWidth: 1040, margin: '0 auto', padding: '24px 20px 80px', display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div>
        <h1 style={{ fontWeight: 700, fontSize: 24, color: 'var(--text)', margin: 0 }}>Cost Report</h1>
        <p style={{ fontWeight: 400, fontSize: 13.5, color: 'var(--muted)', margin: '6px 0 0' }}>
          What running and building the app costs — hosting (AWS) refreshed every 6 hours, development
          (Cursor agent tokens) reconciled hourly. {monthLabel}, internal accounts only.
        </p>
      </div>

      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
        <SummaryCard
          label="Hosting — month to date"
          value={fmtUsd(aws?.monthToDate ?? null)}
          sub={aws ? `whole AWS account · updated ${new Date(aws.updatedAt).toLocaleString()}` : 'waiting for first Cost Explorer pull…'}
        />
        <SummaryCard
          label="Development — month to date"
          value={fmtUsd(dev.cost)}
          sub={`${sessions.length} session${sessions.length === 1 ? '' : 's'} · ${fmtTok(dev.inTok)} in / ${fmtTok(dev.outTok)} out tokens`}
        />
        <SummaryCard label="Combined burn" value={fmtUsd(combined)} sub="hosting + dev sessions" />
      </div>

      <div style={card}>
        <h2 style={h2s}>Hosting — AWS daily spend (last 30 days)</h2>
        {aws?.days?.length ? <DailyBars days={aws.days} /> : <div className="devc-dim">No data yet — the cost worker publishes within ~a minute of backend boot.</div>}
        {services.length > 0 && (
          <table style={{ borderCollapse: 'collapse', width: '100%', marginTop: 16 }}>
            <thead>
              <tr><th style={th}>Service</th><th style={{ ...th, textAlign: 'right' }}>Month to date</th></tr>
            </thead>
            <tbody>
              {services.map(([name, amt]) => (
                <tr key={name}><td style={td}>{name}</td><td style={tdNum}>{fmtUsd(amt)}</td></tr>
              ))}
            </tbody>
          </table>
        )}
        <div className="devc-dim" style={{ marginTop: 10 }}>
          Figures cover the <strong>entire AWS account</strong>, which may also host unrelated
          workloads — the app itself runs on ECS Fargate, load balancing, S3, and
          CloudFront. A per-app split needs cost-allocation tags on the app&apos;s resources. Firebase
          (hosting, auth, Firestore) currently rides the free tier; voice/AI provider keys (OpenAI, xAI)
          bill on their own dashboards and are not included here.
        </div>
      </div>

      <div style={card}>
        <h2 style={h2s}>Smart routing — {monthLabel}</h2>
        {!routingRows.length && <div className="devc-dim">No routed sessions this month yet — sessions started with the &ldquo;Auto — smart routing&rdquo; model pick land here by tier.</div>}
        {routingRows.length > 0 && (
          <>
            <table style={{ borderCollapse: 'collapse', width: '100%' }}>
              <thead>
                <tr>
                  <th style={th}>Tier</th>
                  <th style={{ ...th, textAlign: 'right' }}>Sessions</th>
                  <th style={{ ...th, textAlign: 'right' }}>Share</th>
                  <th style={{ ...th, textAlign: 'right' }}>Cost</th>
                  <th style={{ ...th, textAlign: 'right' }}>Avg / session</th>
                  <th style={{ ...th, textAlign: 'right' }}>Accepted</th>
                  <th style={{ ...th, textAlign: 'right' }}>Failed</th>
                </tr>
              </thead>
              <tbody>
                {routingRows.map((r) => (
                  <tr key={r.tier}>
                    <td style={td}>{r.tier}</td>
                    <td style={tdNum}>{r.n}</td>
                    <td style={tdNum}>{pct(r.n, routedTotal)}</td>
                    <td style={tdNum}>{fmtUsd(r.usd)}</td>
                    <td style={tdNum}>{fmtUsd(r.n ? r.usd / r.n : 0)}</td>
                    <td style={tdNum}>{r.accepted} <span className="devc-dim">({pct(r.accepted, r.n)})</span></td>
                    <td style={tdNum}>{r.failed} <span className="devc-dim">({pct(r.failed, r.n)})</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="devc-dim" style={{ marginTop: 10 }}>
              Decided by {routing.engine__jev ? `Jev ×${routing.engine__jev}` : ''}{routing.engine__jev && routing.engine__rules ? ', ' : ''}{routing.engine__rules ? `rules ×${routing.engine__rules}` : ''}
              {routing.jevAgree != null || routing.jevDisagree ? <> · Jev agreed with the rules {pct(routing.jevAgree || 0, (routing.jevAgree || 0) + (routing.jevDisagree || 0))} of the time{routing.jevErrors ? ` (${routing.jevErrors} Jev errors)` : ''}</> : null}
              {escalations ? <> · {escalations} escalation{escalations === 1 ? '' : 's'}</> : null}
              {routing.capped ? <> · {routing.capped} capped by frontier share</> : null}
              {routing.decisionUsd ? <> · decisions cost {fmtUsd(routing.decisionUsd)}</> : null}
              . Accepted = PR merged, plan approved, or thumbs-up; failed = error, thumbs-down, or an escalated follow-up.
            </div>
          </>
        )}
      </div>

      <div style={card}>
        <h2 style={h2s}>Development sessions — {monthLabel}</h2>
        {!sessions.length && <div className="devc-dim">No dev sessions this month yet.</div>}
        {sessions.length > 0 && (
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <thead>
              <tr>
                <th style={th}>Session</th>
                <th style={th}>Date</th>
                <th style={th}>Model</th>
                <th style={th}>Status</th>
                <th style={{ ...th, textAlign: 'right' }}>Tokens in</th>
                <th style={{ ...th, textAlign: 'right' }}>Tokens out</th>
                <th style={{ ...th, textAlign: 'right' }}>Cost</th>
              </tr>
            </thead>
            <tbody>
              {sessions.map((s) => {
                const c = sessionCostUsd(s.usage);
                return (
                  <tr key={s.id}>
                    <td style={{ ...td, maxWidth: 340, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={s.title}>{s.title || s.id}</td>
                    <td style={td}>{new Date(s.createdAt).toLocaleDateString()}</td>
                    <td style={td}>{s.model || '—'}{s.route?.tier ? <span className="devc-dim"> · {s.route.tier}</span> : null}</td>
                    <td style={td}>{s.status}</td>
                    <td style={tdNum}>{fmtTok(s.usage?.inputTokens)}</td>
                    <td style={tdNum}>{fmtTok(s.usage?.outputTokens)}</td>
                    <td style={tdNum}>{c == null ? (s.usage ? 'pending' : '—') : fmtUsd(c)}</td>
                  </tr>
                );
              })}
              <tr>
                <td style={{ ...td, fontWeight: 700 }} colSpan={4}>Total</td>
                <td style={{ ...tdNum, fontWeight: 700 }}>{fmtTok(dev.inTok)}</td>
                <td style={{ ...tdNum, fontWeight: 700 }}>{fmtTok(dev.outTok)}</td>
                <td style={{ ...tdNum, fontWeight: 700 }}>{fmtUsd(dev.cost)}</td>
              </tr>
            </tbody>
          </table>
        )}
        <div className="devc-dim" style={{ marginTop: 10 }}>
          "pending" = the run finished but Cursor's billing hasn't landed yet — the hourly reconciler
          fills it in. Costs are what was actually charged (plan-included usage shows $0).
        </div>
      </div>
    </div>
  );
}
